import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import Avatar from '../components/Avatar';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const stageLabels = { qualification: 'تصفيات', final: 'نهائي' };
const emptyForm = { participant_id: '', season_year: 2026, stage: 'qualification', score: '', inner_ten: '', rank: '', is_record: 'none', notes: '' };

export default function DisciplineResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'reviewer';
  const isAdmin = user.role === 'admin';

  const [discipline, setDiscipline] = useState(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [participantQuery, setParticipantQuery] = useState('');
  const [participantOptions, setParticipantOptions] = useState([]);
  const [editingResult, setEditingResult] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    const disciplines = await client.get('/disciplines');
    const d = disciplines.data.find(x => String(x.id) === String(id));
    setDiscipline(d);
    const { data } = await client.get('/results', { params: { discipline_id: id, limit: 100 } });
    setRows(data.data);
    client.get(`/results/discipline/${id}/stats`).then(r => setStats(r.data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const searchParticipants = async (q) => {
    setParticipantQuery(q);
    if (q.length < 2) { setParticipantOptions([]); return; }
    const { data } = await client.get('/participants', { params: { search: q, limit: 8 } });
    setParticipantOptions(data.data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setMsg('');
    try {
      await client.post('/results', { ...form, discipline_id: id });
      setMsg('تم تسجيل النتيجة'); setForm(emptyForm); setParticipantQuery('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const saveEdit = async () => {
    await client.put(`/results/${editingResult.id}`, {
      stage: editingResult.stage, score: editingResult.score, inner_ten: editingResult.inner_ten,
      rank: editingResult.rank, is_record: editingResult.is_record, notes: editingResult.notes
    });
    setEditingResult(null);
    load();
  };

  const removeResult = async (rid) => {
    if (!window.confirm('تأكيد حذف هذه النتيجة؟')) return;
    await client.delete(`/results/${rid}`);
    load();
  };

  if (!discipline) return <div className="card">جارٍ التحميل...</div>;

  return (
    <div>
      <button className="btn btn-outline no-print" style={{ marginBottom: 14 }} onClick={() => navigate('/results')}>← كل المسابقات</button>

      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>{discipline.name_ar}</h2>
        <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
          {discipline.gender === 'male' ? 'رجال' : 'سيدات'} · {discipline.qualification_shots} طلقة/هدف · الحد الأقصى {discipline.qualification_max}
        </div>
      </div>

      {stats && stats.total_results > 0 && (
        <div className="card no-print" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>تحليل هذه المسابقة</h3>
          <div className="grid grid-4" style={{ marginBottom: 16 }}>
            <div className="card stat-card"><div className="stat-num">{stats.participant_count}</div><div className="stat-label">عدد الرماة</div></div>
            <div className="card stat-card"><div className="stat-num">{stats.total_results}</div><div className="stat-label">إجمالي النتائج</div></div>
            <div className="card stat-card"><div className="stat-num">{stats.average_score ?? '—'}</div><div className="stat-label">متوسط النتيجة</div></div>
            <div className="card stat-card seal"><div className="stat-num">{stats.best_score ?? '—'}</div><div className="stat-label">أفضل نتيجة</div></div>
          </div>
          <div className="grid grid-2">
            <div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>أفضل 15 نتيجة (تصفيات)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.leaderboard}>
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" height={60} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="score" radius={[6, 6, 0, 0]} fill="#0b8793" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {stats.byCountry.length > 0 && (
              <div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>توزيع الرماة حسب الدولة</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.byCountry}>
                    <XAxis dataKey="country" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {stats.byCountry.map((e, i) => <Cell key={i} fill="#1e3a8a" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {canEdit && (
        <div className="card no-print" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>تسجيل نتيجة جديدة</h3>
          {msg && <div className="alert alert-success">{msg}</div>}
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={submit}>
            <div className="form-grid">
              <div className="form-field" style={{ position: 'relative' }}>
                <label>الرامي (اكتب للبحث) *</label>
                <input value={participantQuery} onChange={e => searchParticipants(e.target.value)} placeholder="اسم اللاعب..." required={!form.participant_id} />
                {participantOptions.length > 0 && (
                  <div className="card" style={{ position: 'absolute', zIndex: 5, width: '100%', padding: 6, marginTop: 4 }}>
                    {participantOptions.map(p => (
                      <div key={p.id} style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13 }}
                        onClick={() => { setForm({ ...form, participant_id: p.id }); setParticipantQuery(p.full_name_ar); setParticipantOptions([]); }}>
                        {p.full_name_ar} — {p.organization_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-field"><label>الموسم</label>
                <input type="number" value={form.season_year} onChange={e => setForm({ ...form, season_year: e.target.value })} /></div>
              <div className="form-field"><label>المرحلة</label>
                <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })}>
                  <option value="qualification">تصفيات</option><option value="final">نهائي</option>
                </select></div>
              <div className="form-field"><label>النتيجة *</label>
                <input type="number" step="0.1" value={form.score} onChange={e => setForm({ ...form, score: e.target.value })} required /></div>
              <div className="form-field"><label>عدد الوسط (Inner Ten)</label>
                <input type="number" value={form.inner_ten} onChange={e => setForm({ ...form, inner_ten: e.target.value })} /></div>
              <div className="form-field"><label>الترتيب</label>
                <input type="number" value={form.rank} onChange={e => setForm({ ...form, rank: e.target.value })} /></div>
              <div className="form-field"><label>رقم قياسي؟</label>
                <select value={form.is_record} onChange={e => setForm({ ...form, is_record: e.target.value })}>
                  <option value="none">لا يوجد</option><option value="national">وطني</option><option value="world">عالمي</option>
                </select></div>
            </div>
            <button className="btn btn-primary" style={{ marginTop: 16 }}>حفظ النتيجة</button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>نتائج {discipline.name_ar} ({rows.length})</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => window.open(`${client.defaults.baseURL}/results/export?discipline_id=${id}&token=${localStorage.getItem('accred_token')}`, '_blank')}>تصدير Excel</button>
            <button className="btn btn-outline" onClick={() => window.print()}>طباعة</button>
          </div>
        </div>
        <table>
          <thead>
            <tr><th></th><th>الرامي</th><th>الجهة</th><th>المرحلة</th><th>النتيجة</th><th>الترتيب</th><th>الموسم</th>{canEdit && <th></th>}</tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td><Avatar src={r.photo_path} name={r.full_name_ar} size={32} /></td>
                <td style={{ color: 'var(--signal-dark)', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate(`/participants/${r.participant_id}`)}>
                  {r.full_name_ar} {r.is_record !== 'none' && <span title={r.is_record}>🏅</span>}
                </td>
                <td>{r.organization_name}</td>
                <td><span className="badge" style={{ background: r.stage === 'final' ? '#6d28d9' : '#9ca3af' }}>{stageLabels[r.stage]}</span></td>
                <td className="mono">{r.score}</td>
                <td>{r.rank ?? '—'}</td>
                <td>{r.season_year}</td>
                {canEdit && (
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-outline" onClick={() => setEditingResult({ ...r })}>تعديل</button>
                    {isAdmin && <button className="btn btn-danger" onClick={() => removeResult(r.id)}>حذف</button>}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={canEdit ? 8 : 7} style={{ textAlign: 'center', color: '#9ca3af' }}>لا توجد نتائج مسجلة بعد</td></tr>}
          </tbody>
        </table>
      </div>

      {editingResult && (
        <div className="card" style={{ marginTop: 20 }}>
          <h4 style={{ marginTop: 0 }}>تعديل نتيجة: {editingResult.full_name_ar}</h4>
          <div className="form-grid">
            <div className="form-field"><label>المرحلة</label>
              <select value={editingResult.stage} onChange={e => setEditingResult({ ...editingResult, stage: e.target.value })}>
                <option value="qualification">تصفيات</option><option value="final">نهائي</option>
              </select></div>
            <div className="form-field"><label>النتيجة</label>
              <input type="number" step="0.1" value={editingResult.score} onChange={e => setEditingResult({ ...editingResult, score: e.target.value })} /></div>
            <div className="form-field"><label>الترتيب</label>
              <input type="number" value={editingResult.rank || ''} onChange={e => setEditingResult({ ...editingResult, rank: e.target.value })} /></div>
            <div className="form-field"><label>رقم قياسي؟</label>
              <select value={editingResult.is_record} onChange={e => setEditingResult({ ...editingResult, is_record: e.target.value })}>
                <option value="none">لا يوجد</option><option value="national">وطني</option><option value="world">عالمي</option>
              </select></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn btn-primary" onClick={saveEdit}>حفظ</button>
            <button className="btn btn-outline" onClick={() => setEditingResult(null)}>إلغاء</button>
          </div>
        </div>
      )}
    </div>
  );
}
